import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Gamepad2,
  Home,
  ShoppingBag,
  Sparkles,
  Trophy,
  WalletCards,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { api } from "../lib/api";
import { bool, isRecord, num, unwrapData } from "../lib/data";

interface TourStep {
  title: string;
  text: string;
  icon: LucideIcon;
}

const steps: TourStep[] = [
  {
    title: "Home",
    text: "Hier bündelt F‑Bucks Wallet, wertvollste Gegenstände, 7‑Tage-Verlauf und alles, was gerade ansteht.",
    icon: Home,
  },
  {
    title: "Wallet & Wochenbonus",
    text: "Bucks sind rein virtuell. Qualifizierte Spiele mit Einsatz zählen für den Bonus am Montag um 00:00 Uhr Europe/Berlin.",
    icon: WalletCards,
  },
  {
    title: "Inventar & Flairs",
    text: "Gegenstände besitzen eine vollständige Historie. Ein primärer und optional ein sekundärer Flair gestalten deinen Auftritt.",
    icon: Sparkles,
  },
  {
    title: "Games & Tische",
    text: "Öffne Games, prüfe Regeln und Einsatz, dann tritt bewusst bei. Der Server validiert jeden Zug.",
    icon: Gamepad2,
  },
  {
    title: "Marketplace",
    text: "Festpreise, Auktionen und direkte Trades reservieren Assets und Bucks sicher bis zur atomaren Übertragung.",
    icon: ShoppingBag,
  },
  {
    title: "Turniere, Pots & Fairness",
    text: "Vor dem Start siehst du Verteilungen und Jackpotregeln. Zufallsergebnisse lassen sich über Provably Fair nachrechnen.",
    icon: Trophy,
  },
  {
    title: "Abstimmungen, Log & Hinweise",
    text: "Community-Entscheidungen, unveränderliche Ereignisse und persönliche Benachrichtigungen bleiben nachvollziehbar.",
    icon: Bell,
  },
];

export function IntroTour({ children }: { children: ReactNode }) {
  const [localStep, setLocalStep] = useState<number | null>(null);
  const query = useQuery({
    queryKey: ["account", "intro"],
    queryFn: () => api.get<unknown>("/account/intro"),
    retry: false,
    staleTime: 60_000,
  });
  const body = unwrapData(query.data);
  const status = isRecord(body) ? body : {};
  const serverStep = num(status.step);
  const visible = query.isSuccess && !bool(status.completed) && !bool(status.skipped);
  const stepIndex = localStep ?? Math.min(serverStep, steps.length - 1);
  const step = steps[stepIndex] ?? steps[0]!;
  const update = useMutation({
    mutationFn: (payload: { step?: number; completed?: boolean; skipped?: boolean }) =>
      api.patch("/account/intro", payload),
  });
  const next = () => {
    if (stepIndex >= steps.length - 1) {
      update.mutate({ completed: true, step: steps.length });
      setLocalStep(steps.length);
    } else {
      const nextStep = stepIndex + 1;
      setLocalStep(nextStep);
      update.mutate({ step: nextStep });
    }
  };
  return (
    <>
      {children}
      {visible && stepIndex < steps.length && (
        <div className="tour-backdrop" role="presentation">
          <section
            className="tour-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tour-title"
          >
            <button
              className="icon-button tour-dialog__close"
              type="button"
              onClick={() => {
                update.mutate({ skipped: true, step: stepIndex });
                setLocalStep(steps.length);
              }}
              aria-label="Intro überspringen"
            >
              <X aria-hidden="true" />
            </button>
            <div className="tour-dialog__progress">
              <span style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }} />
            </div>
            <span className="icon-disc">
              <step.icon aria-hidden="true" />
            </span>
            <p className="eyebrow">
              Kurze Tour · {stepIndex + 1}/{steps.length}
            </p>
            <h2 id="tour-title">{step.title}</h2>
            <p>{step.text}</p>
            <div className="tour-dialog__actions">
              <button
                className="button button--ghost"
                type="button"
                onClick={() => setLocalStep(Math.max(0, stepIndex - 1))}
                disabled={stepIndex === 0}
              >
                <ChevronLeft aria-hidden="true" />
                Zurück
              </button>
              <button className="button button--primary" type="button" onClick={next}>
                {stepIndex === steps.length - 1 ? "Tour abschließen" : "Weiter"}
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
